import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { Connector, CreateDestinationInput, Destination, NamingMode } from "../lib/types";
import { NamingSelect, NAMING_LABEL } from "../components/NamingSelect";
import { PageHeader } from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";
import { Modal } from "../components/Modal";
import { Table, THead, TH, TBody, TR, TD } from "../components/Table";
import { Loading, ErrorState, EmptyState } from "../components/EmptyState";
import { formatBytes } from "../lib/format";

export function DestinationsPage() {
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);

  const destinations = useQuery({
    queryKey: ["destinations"],
    queryFn: () => api.destinations.list(),
  });
  const connectors = useQuery({
    queryKey: ["connectors"],
    queryFn: () => api.connectors.list(),
  });

  const connectorName = (id: string) =>
    connectors.data?.find((c) => c.id === id)?.displayName ?? id;

  const hasConnectors = (connectors.data?.length ?? 0) > 0;

  return (
    <div>
      <PageHeader
        title="Destinations"
        description="A destination scopes a bucket or container, an optional key prefix, and upload constraints under one of your connectors. Keys are issued against destinations."
        action={
          <button
            className="btn-primary"
            onClick={() => setAdding(true)}
            disabled={!hasConnectors}
            title={hasConnectors ? undefined : "Add a connector first"}
          >
            Add destination
          </button>
        }
      />

      {destinations.isLoading ? (
        <Loading />
      ) : destinations.isError ? (
        <ErrorState error={destinations.error} onRetry={destinations.refetch} />
      ) : destinations.data && destinations.data.length > 0 ? (
        <Table>
          <THead>
            <tr>
              <TH>Bucket / container</TH>
              <TH>Connector</TH>
              <TH>Prefix</TH>
              <TH>Max size</TH>
              <TH>Status</TH>
            </tr>
          </THead>
          <TBody>
            {destinations.data.map((d) => (
              <TR key={d.id} onClick={() => navigate(`/destinations/${d.id}`)}>
                <TD>
                  <div className="mono font-medium text-ink">
                    {d.bucketOrContainer}
                  </div>
                  <div className="text-xs text-ink-subtle">
                    {NAMING_LABEL[d.naming]}
                  </div>
                </TD>
                <TD className="text-ink-muted">
                  {connectorName(d.connectorId)}
                </TD>
                <TD className="mono text-xs text-ink-muted">
                  {d.keyPrefix || "—"}
                </TD>
                <TD className="text-ink-muted">{formatBytes(d.maxBytes)}</TD>
                <TD>
                  <StatusBadge health={d.status} />
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      ) : (
        <EmptyState
          title={hasConnectors ? "No destinations yet" : "Add a connector first"}
          description={
            hasConnectors
              ? "Define where uploads land — pick a connector, a bucket/container, and an optional prefix."
              : "Destinations live under a connector. Create a connector before adding destinations."
          }
          action={
            hasConnectors ? (
              <button className="btn-primary" onClick={() => setAdding(true)}>
                Add destination
              </button>
            ) : undefined
          }
        />
      )}

      <AddDestinationModal
        open={adding}
        onClose={() => setAdding(false)}
        connectors={connectors.data ?? []}
      />
    </div>
  );
}

/**
 * Add-destination form. Reused by the Destinations page and the connection
 * detail page; pass `fixedConnectorId` to scope it to one connector (the select
 * is then hidden). This is the complete create surface — every editable field.
 */
export function AddDestinationModal({
  open,
  onClose,
  connectors,
  fixedConnectorId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  connectors: Connector[];
  fixedConnectorId?: string;
  onCreated?: (dest: Destination) => void;
}) {
  const qc = useQueryClient();
  const [connectorId, setConnectorId] = useState(fixedConnectorId ?? "");
  const [bucketOrContainer, setBucketOrContainer] = useState("");
  const [keyPrefix, setKeyPrefix] = useState("");
  const [naming, setNaming] = useState<NamingMode>("unique");
  const [isolateByUser, setIsolateByUser] = useState(false);
  const [maxMb, setMaxMb] = useState("");

  const create = useMutation({
    mutationFn: (input: CreateDestinationInput) =>
      api.destinations.create(input),
    onSuccess: (dest) => {
      qc.invalidateQueries({ queryKey: ["destinations"] });
      reset();
      onCreated?.(dest);
      onClose();
    },
  });

  function reset() {
    setConnectorId(fixedConnectorId ?? "");
    setBucketOrContainer("");
    setKeyPrefix("");
    setNaming("unique");
    setIsolateByUser(false);
    setMaxMb("");
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    create.mutate({
      connectorId: fixedConnectorId || connectorId || connectors[0]?.id || "",
      bucketOrContainer,
      keyPrefix: keyPrefix || undefined,
      naming,
      isolateByUser,
      maxBytes: maxMb ? Number(maxMb) * 1024 * 1024 : undefined,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add destination"
      description="Scope a bucket/container and constraints under a connector."
    >
      <form onSubmit={submit} className="space-y-4">
        {!fixedConnectorId && (
          <div>
            <label className="label" htmlFor="connector">
              Connector
            </label>
            <select
              id="connector"
              className="input"
              value={connectorId}
              onChange={(e) => setConnectorId(e.target.value)}
              required
            >
              <option value="" disabled>
                Select a connector…
              </option>
              {connectors.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.displayName}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="label" htmlFor="bucket">
            Bucket / container
          </label>
          <input
            id="bucket"
            className="input mono"
            placeholder="acme-uploads"
            value={bucketOrContainer}
            onChange={(e) => setBucketOrContainer(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="label" htmlFor="prefix">
            Key prefix <span className="text-ink-subtle">(optional)</span>
          </label>
          <input
            id="prefix"
            className="input mono"
            placeholder="incoming/2026/"
            value={keyPrefix}
            onChange={(e) => setKeyPrefix(e.target.value)}
          />
        </div>

        <div>
          <span className="label">Object naming</span>
          <NamingSelect value={naming} onChange={setNaming} />
        </div>

        <div>
          <span className="label">Privacy</span>
          <label className="flex items-center gap-2 py-1.5 text-sm text-ink">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-surface-300 text-accent focus:ring-accent/30"
              checked={isolateByUser}
              onChange={(e) => setIsolateByUser(e.target.checked)}
            />
            Keep each person's files private (isolate by owner prefix)
          </label>
        </div>

        <div>
          <label className="label" htmlFor="maxmb">
            Max object size (MB){" "}
            <span className="text-ink-subtle">(optional)</span>
          </label>
          <input
            id="maxmb"
            type="number"
            min="1"
            className="input"
            placeholder="500"
            value={maxMb}
            onChange={(e) => setMaxMb(e.target.value)}
          />
        </div>

        {create.isError && (
          <p className="rounded-md bg-danger-soft px-3 py-2 text-sm text-danger">
            {create.error instanceof Error
              ? create.error.message
              : "Could not create destination."}
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={create.isPending}
          >
            {create.isPending ? "Creating…" : "Create destination"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
